import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, List, Settings as SettingsIcon, LogOut, Calendar, Clock, Users, DoorOpen, Target, User, ChevronDown, ChevronUp, MessageCircle, MessageSquare, SlidersHorizontal } from 'lucide-react';
import BASE_URL from './config';
import { useToast, ToastContainer } from './Toast';
import AlertModal from './AlertModal';
import NLSearch from './NLSearch';
import RideChat from './RideChat';
import StarRating from './StarRating';
import './index.css';
import logo from './images/logo.png';
import LOCATIONS, { getLocationDisplay } from './locations';
import useUnreadCounts from './useUnreadCounts';

function Feed() {
  const [rides, setRides] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Toast and Alert modal state
  const { toasts, showToast, removeToast } = useToast();
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });


  

  const navigate = useNavigate();
  const location = useLocation();

  const [filterDate, setFilterDate] = useState('');
  const [filterTime, setFilterTime] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterFromCustom, setFilterFromCustom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterToCustom, setFilterToCustom] = useState('');
  const [sortBy, setSortBy] = useState('upcoming');
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState('');

  const [loadingRides, setLoadingRides] = useState(true);

  // NL search state
  const [nlSearchResults, setNlSearchResults] = useState(null); // null = not searching, array = search results

  // Participants modal state
  const [participants, setParticipants] = useState([]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);

  // Chat state
  const [chatRideId, setChatRideId] = useState(null);

  const unreadCounts = useUnreadCounts();


  // Check login status and fetch user data
  useEffect(() => {
    // ProtectedRoute already handles authentication, so we can assume user is logged in
    // Just fetch user data for the component
    fetch(`${BASE_URL}/check_login`, {
      method: 'GET',
      credentials: 'include'
    })
    .then(response => response.json())
    .then(data => {
      if (data.isLoggedIn) {
        setIsLoggedIn(true);
        setUser(data.user);
        // Feed is for browsing rides - no profile modal needed
        // Profile completion is handled by ProtectedRoute
      }
    })
    .catch(error => {
      // Don't redirect on error - ProtectedRoute handles authentication
    });
  }, []);

  // Fetch all rides (feed view)
  useEffect(() => {
    fetchAllRides();
  }, []);

  // Listen for ride deletion events from other components
  useEffect(() => {
    const handleRideDeleted = (event) => {
      // Refresh the feed data when a ride is deleted
      fetchAllRides();
    };

    window.addEventListener('rideDeleted', handleRideDeleted);
    
    return () => {
      window.removeEventListener('rideDeleted', handleRideDeleted);
    };
  }, []);

  const fetchAllRides = async () => {
    setLoadingRides(true);
    try {
      const response = await fetch(`${BASE_URL}/api/rides`, {
        method: 'GET', // HTTP GET request
        headers: {
          'Accept': 'application/json', // Accept JSON response
          'Content-Type': 'application/json' // Content type is JSON
        },
        credentials: 'include' // Include cookies for authentication/session
      });
      
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content
      const responseText = await response.text();
      
      if (!responseText || responseText.trim() === '') {
        setRides([]);
        return;
      }
      
      const data = JSON.parse(responseText);
      setRides(data); // update the state
    } catch (error) {
      setRides([]); // Set empty array on error
    } finally {
      setLoadingRides(false);
    }
  };


  const goToHome = () => {
    navigate('/feed');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToRides = () => {
    navigate('/rides');
  };

  const goToNewRide = () => {
    navigate('/newride');
  };

  const goToSettings = () => {
    navigate('/settings');
  };

  const handleLogout = () => {
    window.location.href = `${BASE_URL}/logout`;
  };

  // Check if a ride is expired (past departure time)
  const isRideExpired = (ride) => {
    if (!ride || !ride.departureDate) return false; // Can't determine expiration without date
    
    try {
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      today.setMinutes(0, 0, 0);
      
      // Parse the date - dates are in YYYY-MM-DD format
      const dateParts = ride.departureDate.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);
      
      if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
      
      const rideDate = new Date(year, month - 1, day); // month is 0-indexed
      rideDate.setHours(0, 0, 0, 0);
      rideDate.setMinutes(0, 0, 0);
      
      // If the date is in the past, it's expired
      if (rideDate < today) {
        return true;
      }
      
      // If the date is today, check if the time has passed
      if (rideDate.getTime() === today.getTime()) {
        // Use end time if available, otherwise fall back to departure time or start time
        const timeToUse = ride.endTime || ride.departureTime || ride.startTime;
        if (!timeToUse) return false; // If no time, can't determine expiration
        
        const timeParts = timeToUse.split(':');
        if (timeParts.length < 2) return false;
        
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        if (isNaN(hours) || isNaN(minutes)) return false;
        
        const rideDateTime = new Date(year, month - 1, day, hours, minutes);
        return rideDateTime < now;
      }
      
      // If the date is in the future, it's not expired
      return false;
    } catch (error) {
      console.error('Error checking ride expiration:', error, ride);
      return false; // On error, don't expire (safer default)
    }
  };

  // Filter and sort rides
  const filteredAndSortedRides = rides.filter(ride => {
    // First, filter out expired rides
    if (isRideExpired(ride)) {
      return false;
    }
    
    const matchesDate = !filterDate || (() => {
      // Simple direct string comparison since dates are now stored exactly as selected
      const match = filterDate === ride.departureDate;
      
      
      return match;
    })();
    const matchesTime = !filterTime || (() => {
      // If we have start and end times, check if filter time falls within the range
      if (ride.startTime && ride.endTime) {
        return filterTime >= ride.startTime && filterTime <= ride.endTime;
      }
      // If we only have start time, check if filter time matches or is after
      if (ride.startTime) {
        return filterTime >= ride.startTime;
      }
      // If we only have end time, check if filter time matches or is before
      if (ride.endTime) {
        return filterTime <= ride.endTime;
      }
      // Fallback to exact match with departureTime
      return ride.departureTime === filterTime;
    })();
    const rideDestination = ride.toLocation || ride.destination || '';
    const rideOrigin = ride.fromLocation || '';
    const resolvedFrom = filterFrom === '_other' ? `custom:${filterFromCustom}` : filterFrom;
    const resolvedTo = filterTo === '_other' ? `custom:${filterToCustom}` : filterTo;
    const matchesFrom = !resolvedFrom || (resolvedFrom.startsWith('custom:')
      ? rideOrigin.toLowerCase().includes(filterFromCustom.toLowerCase())
      : rideOrigin === resolvedFrom);
    const matchesTo = !resolvedTo || (resolvedTo.startsWith('custom:')
      ? rideDestination.toLowerCase().includes(filterToCustom.toLowerCase())
      : rideDestination === resolvedTo);

    return matchesDate && matchesTime && matchesFrom && matchesTo;
  }).sort((a, b) => {
    if (sortBy === 'upcoming') {
      return new Date(`${a.departureDate} ${a.startTime || a.departureTime}`) - new Date(`${b.departureDate} ${b.startTime || b.departureTime}`);
    } else if (sortBy === 'oldest') {
      return new Date(`${b.departureDate} ${b.startTime || b.departureTime}`) - new Date(`${a.departureDate} ${a.startTime || a.departureTime}`);
    } else if (sortBy === 'alphabetical') {
      return a.destination.localeCompare(b.destination);
    }
    return 0;
  });

  const formatDate = (dateString) => {
    // Fix timezone issue: Force local timezone interpretation
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format time range (start - end)
  const formatTimeRange = (startTime, endTime, fallbackTime) => {
    // If we have both start and end times, show the range
    if (startTime && endTime) {
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    }
    // If we only have start time, show it as a single time
    if (startTime) {
      return formatTime(startTime);
    }
    // If we only have end time, show it as a single time
    if (endTime) {
      return formatTime(endTime);
    }
    // Fallback to the old departureTime field for existing rides
    if (fallbackTime) {
      return formatTime(fallbackTime);
    }
    return 'Time TBD';
  };

  // Format phone number with dashes
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format based on length
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else {
      // Return original if it doesn't match expected format
      return phone;
    }
  };

  // Get full airport name from code
  const getAirportName = (code) => {
    const airports = {
      'LGA': 'LaGuardia Airport',
      'JFK': 'John F. Kennedy Airport',
      'BDL': 'Bradley International Airport',
      'EWR': 'Newark Liberty Airport',
      'Tweed': 'Tweed New Haven Airport'
    };
    return airports[code] || code;
  };

  // Format airport as "CODE - Full Name"
  const formatAirport = (code) => {
    const fullName = getAirportName(code);
    return `${code} - ${fullName}`;
  };

  const joinRide = async (rideId) => {
    try {
      const response = await fetch(`${BASE_URL}/joinride`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ requestID: rideId, username: user })
      });

      if (response.ok) {
        const data = await response.json();
        showToast('Successfully joined the ride!', 'success');
        fetchAllRides(); // Refresh the rides list
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Failed to join ride', 'error');
      }
    } catch (error) {
      showToast('Error joining ride. Please try again.', 'error');
    }
  };

  const leaveRide = async (rideId) => {
    try {
      const response = await fetch(`${BASE_URL}/leaveRide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ rideId: rideId, username: user })
      });

      if (response.ok) {
        showToast('Successfully left the ride!', 'success');
        fetchAllRides(); // Refresh the rides list
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Failed to leave ride', 'error');
      }
    } catch (error) {
      showToast('Error leaving ride. Please try again.', 'error');
    }
  };

  // Get the names and emails of the participants in a given ride given its ride ID
  const viewParticipants = (rideId) => {
    fetch(`${BASE_URL}/rideDetails/${rideId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })
    .then(response => response.json())
    .then(data => {
      setParticipants(data.participants);
      setShowParticipantsModal(true); 
    })
    .catch(error => {
      setAlertModal({
        isOpen: true,
        title: 'Error Loading Data',
        message: 'Failed to load participant details. Please try again.',
        type: 'error'
      });
    });
  };

  const isUserInRide = (ride) => {
    return ride.participants && ride.participants.includes(user);
  };

  // NL search handlers
  const handleNLSearchResults = (results) => {
    setNlSearchResults(results);
  };

  const handleNLSearchClear = () => {
    setNlSearchResults(null);
  };

  // Determine which rides to display: NL search results or filtered rides
  const displayRides = nlSearchResults
    ? nlSearchResults.map(r => r.ride)
    : filteredAndSortedRides;

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="skeleton-card">
      <div className="skeleton-header">
        <div className="skeleton skeleton-badge"></div>
      </div>
      <div className="skeleton skeleton-line long"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-button"></div>
    </div>
  ); 

  return (
    <div className="app-container">

      {/* Header on top that allows the user to navigate to different pages */}
      <header className="header">
        <div className="logo-container" onClick={goToHome}>
          <div className="logo-wrapper logo-light-mode">
            <img 
              src={logo} 
              alt="Logo" 
              className="logo"
            />
          </div>
          <h1>Bulldog Carpool</h1>
        </div>
        
        
        {/* Desktop Navigation */}
        <nav className="nav-buttons">
          <button
            className="nav-button active"
            onClick={() => navigate('/feed')}
          >
            <List className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Feed
          </button>
          <button
            className="nav-button"
            onClick={() => navigate('/rides')}
          >
            <Car className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            My Rides{unreadCounts.ride_join > 0 && <span className="nav-badge">{unreadCounts.ride_join}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/messages')}>
            <MessageSquare className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Messages{unreadCounts.message > 0 && <span className="nav-badge">{unreadCounts.message}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/settings')}>
            <SettingsIcon className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Settings
          </button>
          <button className="nav-button" onClick={handleLogout}>
            <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Log Out
          </button>
        </nav>
        
      </header>
      
      <div className="rides-dashboard">
          {/* Button that allows users to create a new ride */}
          <button className="add-ride-button" onClick={goToNewRide}>Add Ride</button>

          {/* Feed Content */}
          <div className="tab-content">
            <h2>Available Rides</h2>

            {/* Natural Language Search */}
            <NLSearch
              onResults={handleNLSearchResults}
              onClear={handleNLSearchClear}
              user={user}
            />

            {!nlSearchResults && (
            <div className="filters">
              <div className="filter-header" onClick={() => setFiltersCollapsed(!filtersCollapsed)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <SlidersHorizontal className="w-4 h-4" stroke="#6B7280" strokeWidth={1.5} />
                  <span style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '500' }}>Advanced Filters</span>
                </div>
                <button className="collapse-toggle">
                  {filtersCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
              </div>
              {!filtersCollapsed && (
                <div className="filter-grid">
                <div className="filter-field">
                  <label htmlFor="filterFrom">From:</label>
                  <select
                    id="filterFrom"
                    value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                  >
                    <option value="">All Origins</option>
                    <optgroup label="Campus">
                      {LOCATIONS.filter(l => l.type === 'campus').map(l => (
                        <option key={l.key} value={l.key}>{l.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Airports">
                      {LOCATIONS.filter(l => l.type === 'airport').map(l => (
                        <option key={l.key} value={l.key}>{l.label}</option>
                      ))}
                    </optgroup>
                    <option value="_other">Other...</option>
                  </select>
                  {filterFrom === '_other' && (
                    <input
                      type="text"
                      value={filterFromCustom}
                      onChange={(e) => setFilterFromCustom(e.target.value)}
                      placeholder="Type origin..."
                      className="custom-location-input"
                    />
                  )}
                </div>

                <div className="filter-field">
                  <label htmlFor="filterTo">To:</label>
                  <select
                    id="filterTo"
                    value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                  >
                    <option value="">All Destinations</option>
                    <optgroup label="Airports">
                      {LOCATIONS.filter(l => l.type === 'airport').map(l => (
                        <option key={l.key} value={l.key}>{l.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Campus">
                      {LOCATIONS.filter(l => l.type === 'campus').map(l => (
                        <option key={l.key} value={l.key}>{l.label}</option>
                      ))}
                    </optgroup>
                    <option value="_other">Other...</option>
                  </select>
                  {filterTo === '_other' && (
                    <input
                      type="text"
                      value={filterToCustom}
                      onChange={(e) => setFilterToCustom(e.target.value)}
                      placeholder="Type destination..."
                      className="custom-location-input"
                    />
                  )}
                </div>

                <div className="filter-field">
                  <label htmlFor="date">Date:</label>
                  <input
                    type="date"
                    id="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </div>

                <div className="filter-field">
                  <label htmlFor="time">Time:</label>
                  <input
                    type="time"
                    id="time"
                    value={filterTime}
                    onChange={(e) => setFilterTime(e.target.value)}
                  />
                </div>

                <div className="filter-actions">
                  <button className="clear-filters-btn" onClick={() => {
                    setFilterFrom('');
                    setFilterFromCustom('');
                    setFilterTo('');
                    setFilterToCustom('');
                    setFilterDate('');
                    setFilterTime('');
                  }}>Clear Filters</button>
                </div>
              </div>
              )}
            </div>
            )}

            {!nlSearchResults && (
              <div className="sort-section">
                <div></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label htmlFor="sortBy">Sort By:</label>
                  <select
                    id="sortBy"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-dropdown"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="oldest">Oldest</option>
                    <option value="alphabetical">Alphabetical (A-Z)</option>
                  </select>
                </div>
              </div>
            )}

            <div className="open-requests">
              {/* Shows a list of all rides that have been created by all users along with all of those rides' information */}
            {loadingRides ? (
              // Show skeleton cards while loading
              <>
                  <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : displayRides.length > 0 ? (
                displayRides.map((ride, index) => {
                  const isOwner = ride.owner === user || ride.owner_username === user;
                  const isJoined = ride.participants && ride.participants.includes(user);
                  const isInRide = isOwner || isJoined;
                  const seatsLeft = ride.spotsAvailable - ride.spotsFilled;
                  
                  return (
                    <div key={ride.requestId} className="ride-card" style={{ animationDelay: `${index * 0.1}s` }}>
                      {/* Status Badge */}
                      {isOwner ? (
                        <span className="status-badge status-joined">Your Ride</span>
                      ) : isJoined ? (
                        <span className="status-badge status-joined">Joined</span>
                      ) : seatsLeft === 0 ? (
                        <span className="status-badge status-full">Full</span>
                      ) : (
                        <span className="status-badge status-available">{seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left</span>
                      )}

                      {/* Trip Info */}
                      <div className="trip-info">
                        <div className="trip-section">
                          <div className="trip-label">Leaving from</div>
                          <div className="trip-value">{getLocationDisplay(ride.fromLocation) || 'New Haven, CT'}</div>
                        </div>
                        <div className="trip-section">
                          <div className="trip-label">Going to</div>
                          <div className="trip-value">{getLocationDisplay(ride.toLocation || ride.destination)}</div>
                        </div>
                        <div className="trip-meta">
                          <div className="trip-date">
                            <Calendar className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                            {formatDate(ride.departureDate)}
                          </div>
                          <div className="trip-time">
                            <Clock className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                            {formatTimeRange(ride.startTime, ride.endTime, ride.departureTime)}
                          </div>
                          <div className="trip-seats">
                            <Users className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                            {ride.spotsFilled}/{ride.spotsAvailable} seats
                          </div>
                        </div>
                        <div className="trip-notes" style={!ride.notes ? { color: '#9CA3AF', fontStyle: 'italic' } : {}}>
                          {ride.notes ? (ride.notes.length > 80 ? ride.notes.slice(0, 80) + '...' : ride.notes) : 'No additional notes'}
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="card-divider"></div>

                      {/* Driver Info */}
                      <div className="driver-info">
                        <div className="driver-info-row">
                          <div className="driver-avatar">
                            {ride.owner ? ride.owner.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'D'}
                          </div>
                          <div className="driver-details">
                            <div className="driver-name">{ride.owner || 'Driver'}</div>
                            <div className="driver-contact">
                              {ride.owner_email && (
                                <div className="driver-email">{ride.owner_email}</div>
                              )}
                              {ride.owner_phone && (
                                <div className="driver-phone">{formatPhoneNumber(ride.owner_phone)}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="action-buttons">
                        {isOwner ? (
                          <button 
                            className="action-btn action-btn-disabled" 
                            disabled
                          >
                            Your Ride
                          </button>
                        ) : isJoined ? (
                          <button 
                            className="action-btn action-btn-danger" 
                            onClick={() => leaveRide(ride.requestId)}
                          >
                            <DoorOpen className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                            Leave Ride
                          </button>
                        ) : seatsLeft > 0 ? (
                <button 
                            className="action-btn action-btn-primary" 
                            onClick={() => joinRide(ride.requestId)}
                          >
                            Join Ride
                          </button>
                        ) : (
                          <button className="action-btn action-btn-disabled" disabled>
                            Ride Full
                </button>
                        )}
                        
                        {/* View Participants Button */}
                        <button
                          className="action-btn action-btn-secondary"
                          onClick={() => viewParticipants(ride.requestId)}
                        >
                          <User className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                          View Participants
                        </button>
                        {/* Chat Button - only for ride participants */}
                        {isInRide && (
                          <button
                            className="action-btn action-btn-secondary"
                            onClick={() => setChatRideId(ride.requestId)}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                            Chat
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <Target className="empty-state-icon w-12 h-12" stroke="#00356B" strokeWidth={1.5} />
                  <h3>No rides available</h3>
                  <p>Be the first to create a ride!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shows a list of all of the names and emails in a rides that the current user is a part of after clicking the "View Details" button */}
      {showModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#00356B' }}>Ride Details</h2>
              <button 
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ color: '#666' }}>
              <p>This feature will show detailed information about the ride participants.</p>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Alert modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="modal-overlay" onClick={() => setShowParticipantsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '1.25rem', 
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Ride Participants
              </h3>
              <button 
                onClick={() => setShowParticipantsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Participants List */}
            <div style={{ marginBottom: '24px' }}>
              {participants.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {participants.map((participant, index) => (
                    <div key={index} style={{
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontWeight: '500', color: '#1F2937', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {participant.name || 'Unknown User'}
                        {participant.rating && participant.rating.count > 0 && (
                          <StarRating
                            rating={participant.rating.average}
                            count={participant.rating.count}
                            size="sm"
                          />
                        )}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '2px' }}>
                        {participant.email || 'No email provided'}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                        {participant.phone || 'No phone provided'}
                      </div>
              </div>
                  ))}
              </div>
              ) : (
                <p style={{ 
                  textAlign: 'center', 
                  color: '#6B7280',
                  fontStyle: 'italic',
                  padding: '20px'
                }}>
                  No participants found
                </p>
              )}
              </div>
              
            {/* Close Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                onClick={() => setShowParticipantsModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#00356B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Close
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Ride Chat */}
      <RideChat
        rideId={chatRideId}
        currentUser={user}
        isOpen={chatRideId !== null}
        onClose={() => setChatRideId(null)}
      />
        </div>
  );
}

export default Feed;
